// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Pointer Handler Hook
// ═══════════════════════════════════════════════════════════════
// Extracted from PixAlloy.jsx. Returns pointer event handlers.
// All dependencies passed directly — no context, no shared state.
//
// Returns: { gp, onDown, onMove, onUp, onLeave, onDblClick }
// ═══════════════════════════════════════════════════════════════

import { useCallback } from "react";
import { C, T } from "../engine/index.js";
import { hx2 } from "../ui/components.jsx";
import { CURS } from "../tools/toolDefs.jsx";
import {
  penBounds, penApplyTransform, genShape, drawPenPath,
  mkPt, mkPath, pathBounds, pathListBounds, pathListToFlat,
  hitTestPoints, hitTestPathBBox, isNearFirstPoint, closePath,
  updatePoint, appendPoint, removePoint, transformPath, totalPointCount,
  hitTestSegment, splitSegmentAt,
} from "../tools/penTool.js";
import { RB_TFM_DATA } from "@alloyhead/core";

export function usePointer({
  // Refs
  cvs, eng, panState, vpRef,
  toolR, rKeyHeldRef, spaceHeldRef, workerModeRef,
  ss, sd, drag, lassoRef, polyRef,
  bcolRef, fillModeRef, fillCol2Ref, gradModeRef, gradStopsRef,
  eyedropSampleRef, wandTolRef, penPathRef, penDragRef, penPresetRef, penUndoRef, penRedoRef,
  readbackViewRef, guidesRef, bridgeRef, textSeedRef, activePathIdxRef,
  // Callbacks
  wp, pc, fu,
  applyVPTransform, setRotDisplay,
  setCurPos, setCurRGB, setBcol, setGuides, setNearGuide, setTfmCursor,
  setPenPath, setPenPathWithUndo, setPenHover, setShowText, setSelCtxMenu, setActivePathIdx,
  rbHasSel, rbHasTfm, rbActIdx,
  snapToGuides, snapRect, setTool,
}) {
  const gp=useCallback(e=>{const c=cvs.current;if(!c)return{x:0,y:0};const r=c.getBoundingClientRect();
    // Account for CSS rotation: inverse-rotate the mouse coords around canvas center
    const rot=panState.current.rotation*Math.PI/180;
    if(Math.abs(rot)<0.001)return{x:(e.clientX-r.left)*(c.width/r.width),y:(e.clientY-r.top)*(c.height/r.height)};
    const cx2=r.left+r.width/2,cy2=r.top+r.height/2;
    const dx=e.clientX-cx2,dy=e.clientY-cy2;
    const cos=Math.cos(-rot),sin=Math.sin(-rot);
    const rx=dx*cos-dy*sin+r.width/2,ry=dx*sin+dy*cos+r.height/2;
    return{x:rx*(c.width/r.width),y:ry*(c.height/r.height)};
  },[]);

  const onDown=useCallback(e=>{
    // Capture pointer pressure (0-1, mouse defaults to 0.5 on press)
    const prs=e.pressure>0?e.pressure:1;
    wp("pressure",prs);
    // Capture pen tilt (3.5) — 0 for mouse, -90..90 for pen
    if(e.tiltX!=null)wp("tiltX",e.tiltX);
    if(e.tiltY!=null)wp("tiltY",e.tiltY);
    // R key held = canvas rotation drag
    if(rKeyHeldRef.current){
      const vp=vpRef.current;if(!vp)return;
      const rect=vp.getBoundingClientRect();
      const cx2=rect.left+rect.width/2,cy2=rect.top+rect.height/2;
      panState.current.isRotating=true;
      panState.current._rotStartAngle=Math.atan2(e.clientY-cy2,e.clientX-cx2);
      panState.current._rotOrigRot=panState.current.rotation;
      return;
    }
    // Right-click: let context menu handle it, don't process as tool action
    if(e.button===2)return;
    // Spacebar temporary pan (1.2) — pan regardless of current tool
    if(spaceHeldRef?.current){panState.current.isPanning=true;panState.current.lastMX=e.clientX;panState.current.lastMY=e.clientY;return;}
    if(toolR.current===T.PAN||e.button===1){panState.current.isPanning=true;panState.current.lastMX=e.clientX;panState.current.lastMY=e.clientY;return;}
    if(toolR.current===T.ZOOM){
      if(e.altKey){panState.current.zoom=Math.max(panState.current.zoom/1.5,0.1);}
      else{panState.current.zoom=Math.min(panState.current.zoom*1.5,8);}
      applyVPTransform();return;
    }
    const p=gp(e);

    // Hit-test guides — only when Move tool is active
    if(toolR.current===T.MOVE){
      const gs=guidesRef.current;
      const hitThreshold=5;
      for(let gi=0;gi<gs.length;gi++){
        const g=gs[gi];
        const dist=g.type==="h"?Math.abs(p.y-g.pos):Math.abs(p.x-g.pos);
        if(dist<=hitThreshold){
          drag.current={type:"guide",idx:gi,guideType:g.type,origPos:g.pos};
          return;
        }
      }
    }

    wp("cursorX",p.x);wp("cursorY",p.y);wp("cursorDown",1);setCurPos(p);
    const tl=toolR.current;
    const isSelTool=tl===T.SEL_RECT||tl===T.SEL_ELLIPSE||tl===T.LASSO||tl===T.POLY_LASSO||tl===T.WAND;
    const isPaintTool=tl===T.BRUSH||tl===T.ERASER||tl===T.DODGE||tl===T.BURN||tl===T.DISTORT||tl===T.SPOT_HEAL;
    const keepsSel=isPaintTool||tl===T.FILL||tl===T.RUBBER_STAMP||tl===T.GRADIENT||tl===T.RED_EYE;
    // Selection persists for painting tools (constrains where they paint) and for transform.
    // Clear selection for non-painting, non-selection, non-transform tools (Move, Crop, Eyedrop, Text, Pan).
    if(!isSelTool&&!keepsSel&&tl!==T.TRANSFORM&&rbHasSel()){pc(C.SEL_CLR);fu();}
    // Track whether shift is held for additive selection, alt for subtractive
    const shiftHeld=e.shiftKey;
    const altHeld2=e.altKey;
    if(isPaintTool)pc(C.STROKE_ON);
    else if(isSelTool){
      // Selection tool click handling:
      // Shift held = additive mode (add new selection to existing)
      // Alt held = subtractive mode (subtract new selection from existing)
      // No modifier = replace mode (clear existing, start fresh)
      const es=eng.current?.s;
      const hasExisting=workerModeRef.current?rbHasSel():(es?.sel&&es.sel.mask);
      if(hasExisting&&!shiftHeld&&!altHeld2){
        if(!workerModeRef.current&&es){
          // Single-thread: click outside deselects, click inside clears old to start new
          const ix=Math.round(p.x),iy=Math.round(p.y);
          const inBounds=ix>=0&&ix<(es.w||0)&&iy>=0&&iy<(es.h||0);
          const insideSel=inBounds&&es.sel.mask[iy*es.w+ix]>=128;
          if(!insideSel){pc(C.SEL_CLR);fu();return;}
        }
        if(tl!==T.POLY_LASSO){pc(C.SEL_CLR);}
      }
      // With shift/alt: keep existing selection, new one will be merged/subtracted on mouseUp
      ss.current=ss.current||{};
      ss.current._additive=shiftHeld;
      ss.current._subtract=altHeld2;
      // Now dispatch to specific selection tool
      if(tl===T.WAND){pc(C.SEL_WAND,{x:p.x,y:p.y,tolerance:wandTolRef.current,add:shiftHeld,sub:altHeld2});fu();}
      else if(tl===T.SEL_RECT||tl===T.SEL_ELLIPSE){ss.current={x:p.x,y:p.y,_additive:shiftHeld,_subtract:altHeld2};sd.current={t:tl===T.SEL_RECT?"rect":"ellipse",x:p.x,y:p.y,w:0,h:0};}
      else if(tl===T.LASSO){lassoRef.current=[{x:p.x,y:p.y}];sd.current={t:"lasso",points:[{x:p.x,y:p.y}],x:p.x,y:p.y,w:0,h:0};ss.current={_additive:shiftHeld,_subtract:altHeld2};}
      else if(tl===T.POLY_LASSO){
        if(!polyRef.current.length){polyRef.current=[{x:p.x,y:p.y}];ss.current={_additive:shiftHeld,_subtract:altHeld2};}
        else polyRef.current.push({x:p.x,y:p.y});
        sd.current={t:"polyLasso",points:[...polyRef.current],x:p.x,y:p.y,w:0,h:0};
      }
    }
    else if(tl===T.EYEDROP){const c=cvs.current;if(c&&!workerModeRef.current){
      const sz2=eyedropSampleRef.current,half=Math.floor(sz2/2);
      const cx2=Math.round(p.x),cy2=Math.round(p.y);
      const ctx2=c.getContext("2d",{willReadFrequently:true});
      if(sz2<=1){const px=ctx2.getImageData(cx2,cy2,1,1).data;setBcol("#"+[px[0],px[1],px[2]].map(c2=>c2.toString(16).padStart(2,"0")).join(""));}
      else{
        const x0=Math.max(0,cx2-half),y0=Math.max(0,cy2-half);
        const x1=Math.min(c.width,cx2+half+1),y1=Math.min(c.height,cy2+half+1);
        const rw=x1-x0,rh=y1-y0;
        if(rw>0&&rh>0){
          const id2=ctx2.getImageData(x0,y0,rw,rh).data;
          let rr=0,gg=0,bb=0,cnt=0;
          for(let i=0;i<id2.length;i+=4){rr+=id2[i];gg+=id2[i+1];bb+=id2[i+2];cnt++;}
          if(cnt>0)setBcol("#"+[Math.round(rr/cnt),Math.round(gg/cnt),Math.round(bb/cnt)].map(c2=>c2.toString(16).padStart(2,"0")).join(""));
        }
      }
    }}
    else if(tl===T.MOVE)drag.current={type:"move",startX:p.x,startY:p.y,dx:0,dy:0};
    // Phase 4: Red-Eye — click to apply (Spot Heal handled via STROKE_ON + tick)
    else if(tl===T.RED_EYE){pc(C.RED_EYE,{x:p.x,y:p.y,size:eng.current?.s.p.brushSize||30});fu();}
    else if(tl===T.CROP)drag.current={type:"crop",startX:p.x,startY:p.y,x:p.x,y:p.y,w:0,h:0};
    else if(tl===T.TRANSFORM){
      // Check if transform is active from readback SAB or engine
      const tfmActive=rbHasTfm();
      const tf=eng.current?.s.tfm;
      // Read transform bounds from either engine state (single-thread) or readback SAB (worker)
      let hx2,hy2,hw,hh,rot3Deg,selOnly2,isWorkerTfm=false;
      if(tfmActive&&tf){
        hx2=tf.x;hy2=tf.y;hw=tf.w;hh=tf.h;rot3Deg=tf.rot||0;selOnly2=!!tf.selOnly;
      } else if(tfmActive&&!tf){
        const rv=readbackViewRef.current;
        if(!rv){pc(C.TFM_BEGIN);return;}
        const base=RB_TFM_DATA;
        hx2=rv.getFloat32(base,true);hy2=rv.getFloat32(base+4,true);
        hw=rv.getFloat32(base+8,true);hh=rv.getFloat32(base+12,true);
        rot3Deg=rv.getFloat32(base+16,true);
        selOnly2=rv.getUint32(base+28,true)===1;
        isWorkerTfm=true;
      } else {pc(C.TFM_BEGIN);return;}
      // Shared hit-testing for both modes
      const hs=8;
      const rot3=rot3Deg*Math.PI/180;
      const tcx=hx2+hw/2,tcy=hy2+hh/2;
      const cos3=Math.cos(-rot3),sin3=Math.sin(-rot3);
      const lx=(p.x-tcx)*cos3-(p.y-tcy)*sin3,ly=(p.x-tcx)*sin3+(p.y-tcy)*cos3;
      const localP={x:lx+tcx,y:ly+tcy};
      const rlx=lx,rly=ly;
      // Rotation handle
      if(Math.abs(rlx)<8&&Math.abs(rly-(-hh/2-22))<8){
        drag.current={type:"tfm",handle:"rotate",startX:p.x,startY:p.y,origRot:rot3Deg,cx:tcx,cy:tcy,isWorker:isWorkerTfm,selOnly:selOnly2,shiftKey:e.shiftKey};return;
      }
      // Corner + edge handles
      const handles=[
        {id:"tl",cx:hx2,cy:hy2},{id:"tr",cx:hx2+hw,cy:hy2},
        {id:"bl",cx:hx2,cy:hy2+hh},{id:"br",cx:hx2+hw,cy:hy2+hh},
        {id:"t",cx:hx2+hw/2,cy:hy2},{id:"b",cx:hx2+hw/2,cy:hy2+hh},
        {id:"l",cx:hx2,cy:hy2+hh/2},{id:"r",cx:hx2+hw,cy:hy2+hh/2},
      ];
      let hitH=null;
      for(const hd of handles){if(Math.abs(localP.x-hd.cx)<hs&&Math.abs(localP.y-hd.cy)<hs){hitH=hd.id;break;}}
      const inside=!hitH&&localP.x>=hx2&&localP.x<=hx2+hw&&localP.y>=hy2&&localP.y<=hy2+hh;
      if(hitH||inside){
        drag.current={type:"tfm",handle:hitH||"move",startX:p.x,startY:p.y,origX:hx2,origY:hy2,origW:hw,origH:hh,shiftKey:e.shiftKey,isWorker:isWorkerTfm,selOnly:selOnly2};
      } else {
        // Click outside = apply
        if(selOnly2){pc(C.TFM_SEL_APPLY);fu();}
        else{pc(C.TFM_APPLY);fu();}
      }
    }
    else if(tl===T.TEXT){
      // If active layer is a text layer, edit it; otherwise create new at click position
      const actL=eng.current?.s.layers?.[eng.current?.s.act];
      if(actL?.textData){
        textSeedRef.current={...actL.textData}; // edit existing
      } else {
        textSeedRef.current={x:Math.round(p.x),y:Math.round(p.y)}; // new at click pos
      }
      setShowText(true);
    }
    else if(tl===T.FILL){
      const br=bridgeRef.current;const fr=br?br.getParam("brushR"):132,fg=br?br.getParam("brushG"):17,fb=br?br.getParam("brushB"):22,fop=br?br.getParam("brushOpacity"):1;
      const fm=fillModeRef.current;
      if(fm==="gradient-linear"||fm==="gradient-radial"){
        // Start gradient drag — store start point, apply on mouseUp
        drag.current={type:"gradient",startX:p.x,startY:p.y,endX:p.x,endY:p.y,mode:fm,r:fr,g:fg,b:fb,...hx2(fillCol2Ref.current),opacity:fop};
      } else {
        pc(C.FILL,{x:p.x,y:p.y,mode:fm,r:fr,g:fg,b:fb,...hx2(fillCol2Ref.current),opacity:fop,tolerance:wandTolRef.current});fu();
      }
    }
    else if(tl===T.RUBBER_STAMP){if(e.altKey){pc(C.RUBBER_STAMP_SET,{x:p.x,y:p.y});}else if(eng.current?.s.stampSrc){const ss2=eng.current.s.stampSrc;if(ss2.firstClick){ss2.offX=ss2.srcX-p.x;ss2.offY=ss2.srcY-p.y;ss2.firstClick=false;eng.current.s.stampCompDirty=false;}pc(C.STROKE_ON);}else{/* Worker mode: just start stroke, worker handles stamp state */pc(C.STROKE_ON);}}
    else if(tl===T.PEN){
      // Shift+click = start a brand new path (added to the list)
      if(e.shiftKey){
        const newPath=mkPath([mkPt(p.x,p.y)],false);
        setPenPathWithUndo(pl=>[...pl,newPath]);
        setActivePathIdx(penPathRef.current.length); // will resolve to last index
        penDragRef.current={pathIdx:penPathRef.current.length,pointIdx:0,handle:"new",startX:p.x,startY:p.y};
        return;
      }
      // Shape preset mode: drag to draw shape (added as new path)
      const preset=penPresetRef.current;
      if(preset&&preset!=="none"){
        penUndoRef.current.push(JSON.parse(JSON.stringify(penPathRef.current)));penRedoRef.current=[];
        drag.current={type:"penShape",startX:p.x,startY:p.y,endX:p.x,endY:p.y,shape:preset,existingPaths:[...penPathRef.current]};
        return;
      }
      const pathList=penPathRef.current;
      // 1) Hit-test existing anchor/handle points across all paths
      const hit=hitTestPoints(pathList,p.x,p.y,8);
      if(hit){
        const {pathIdx,pointIdx,handle}=hit;
        // Clicking first point of a path with 3+ points = close that path
        if(handle==="anchor"&&pointIdx===0&&pathList[pathIdx].points.length>=3&&!pathList[pathIdx].closed){
          setPenPathWithUndo(pl=>pl.map((pa,i)=>i===pathIdx?closePath(pa):pa));
          setActivePathIdx(pathIdx);
          return;
        }
        // Alt+click on anchor = convert smooth to corner (delete handles)
        if(handle==="anchor"&&e.altKey){
          setPenPathWithUndo(pl=>updatePoint(pl,pathIdx,pointIdx,pt=>({...pt,cp1x:null,cp1y:null,cp2x:null,cp2y:null})));
          setActivePathIdx(pathIdx);
          return;
        }
        // Ctrl+click on anchor = delete the point
        if(handle==="anchor"&&(e.ctrlKey||e.metaKey)){
          const path=pathList[pathIdx];
          if(path.points.length<=2){
            // Removing would leave <2 points — delete the entire path
            if(pathList.length<=1){setPenPathWithUndo([]);}
            else{
              setPenPathWithUndo(pl=>pl.filter((_,i)=>i!==pathIdx));
              setActivePathIdx(Math.max(0,pathIdx>=pathList.length-1?pathIdx-1:pathIdx));
            }
          } else {
            setPenPathWithUndo(pl=>removePoint(pl,pathIdx,pointIdx));
          }
          setActivePathIdx(pathIdx<pathList.length-1?pathIdx:Math.max(0,pathIdx-1));
          return;
        }
        penUndoRef.current.push(JSON.parse(JSON.stringify(pathList)));penRedoRef.current=[];
        setActivePathIdx(pathIdx);
        if(handle==="anchor"){
          penDragRef.current={pathIdx,pointIdx,handle:"anchor",startX:p.x,startY:p.y,origX:pathList[pathIdx].points[pointIdx].x,origY:pathList[pathIdx].points[pointIdx].y};
        } else {
          // Alt+drag handle = independent mode (break symmetry)
          penDragRef.current={pathIdx,pointIdx,handle,startX:p.x,startY:p.y,altKey:e.altKey};
        }
        return;
      }
      // 1b) Check if clicking near a bezier segment (split to add anchor)
      const segHit=hitTestSegment(pathList,p.x,p.y,8);
      if(segHit){
        setPenPathWithUndo(pl=>splitSegmentAt(pl,segHit.pathIdx,segHit.segIdx,segHit.t));
        setActivePathIdx(segHit.pathIdx);
        return;
      }
      // 2) Check per-path bounding box for transform handles
      // Use the active path's bounding box, or check all
      const activePI=activePathIdxRef.current!=null?activePathIdxRef.current:(pathList.length-1);
      const activePath=pathList[activePI];
      if(activePath&&activePath.points.length>=2){
        const b=pathBounds(activePath);
        if(b&&b.w>1&&b.h>1){
          const hs=6;
          const corners=[{h:"tl",x:b.x,y:b.y},{h:"tr",x:b.x+b.w,y:b.y},{h:"br",x:b.x+b.w,y:b.y+b.h},{h:"bl",x:b.x,y:b.y+b.h}];
          for(const c of corners){
            if(Math.abs(p.x-c.x)<hs*2&&Math.abs(p.y-c.y)<hs*2){
              drag.current={type:"penTfm",mode:"scale",handle:c.h,startX:p.x,startY:p.y,bounds:{...b},origPaths:JSON.parse(JSON.stringify(pathList)),targetPathIdx:activePI};return;
            }
          }
          const rotX=b.cx,rotY=b.y-25;
          if(Math.abs(p.x-rotX)<hs*2&&Math.abs(p.y-rotY)<hs*2){
            drag.current={type:"penTfm",mode:"rotate",startAngle:Math.atan2(p.y-b.cy,p.x-b.cx),bounds:{...b},origPaths:JSON.parse(JSON.stringify(pathList)),targetPathIdx:activePI};return;
          }
          // Inside bounding box = move this path
          if(p.x>=b.x-4&&p.x<=b.x+b.w+4&&p.y>=b.y-4&&p.y<=b.y+b.h+4){
            drag.current={type:"penTfm",mode:"move",startX:p.x,startY:p.y,origPaths:JSON.parse(JSON.stringify(pathList)),targetPathIdx:activePI};return;
          }
        }
      }
      // 3) Check if click is inside any other path's bbox (switch active)
      const bboxHit=hitTestPathBBox(pathList,p.x,p.y);
      if(bboxHit>=0){
        setActivePathIdx(bboxHit);
        return; // Just select that path, don't add a point
      }
      // 4) No hit — add new point to the active path, or start new path if none
      if(pathList.length===0||activePath?.closed){
        // Start a new open path
        const newPath=mkPath([mkPt(p.x,p.y)],false);
        setPenPathWithUndo(pl=>[...pl,newPath]);
        const newPI=pathList.length;
        setActivePathIdx(newPI);
        penDragRef.current={pathIdx:newPI,pointIdx:0,handle:"new",startX:p.x,startY:p.y};
      } else {
        // Append to active path
        const pi=activePI;
        const ptIdx=pathList[pi].points.length;
        setPenPathWithUndo(pl=>appendPoint(pl,pi,mkPt(p.x,p.y)));
        penDragRef.current={pathIdx:pi,pointIdx:ptIdx,handle:"new",startX:p.x,startY:p.y};
      }
    }
    else if(tl===T.GRADIENT){
      // Start gradient drag with multi-stop support
      const br2=bridgeRef.current;const fop=br2?br2.getParam("brushOpacity"):1;
      const gm=gradModeRef.current;
      const stops=gradStopsRef.current;
      drag.current={type:"gradient",startX:p.x,startY:p.y,endX:p.x,endY:p.y,mode:gm,r:0,g:0,b:0,r2:0,g2:0,b2:0,opacity:fop,stops};
    }
  },[gp,wp,pc,fu]);

  const onMove=useCallback(e=>{
    // Update pressure on every move
    if(e.pressure>0)wp("pressure",e.pressure);
    // Update tilt on every move (3.5)
    if(e.tiltX!=null)wp("tiltX",e.tiltX);
    if(e.tiltY!=null)wp("tiltY",e.tiltY);
    // Canvas rotation drag
    if(panState.current.isRotating){
      const vp=vpRef.current;if(!vp)return;
      const rect=vp.getBoundingClientRect();
      const cx2=rect.left+rect.width/2,cy2=rect.top+rect.height/2;
      const angle=Math.atan2(e.clientY-cy2,e.clientX-cx2);
      const delta=(angle-panState.current._rotStartAngle)*180/Math.PI;
      panState.current.rotation=panState.current._rotOrigRot+delta;
      applyVPTransform();setRotDisplay(Math.round(panState.current.rotation%360));return;
    }
    if(panState.current.isPanning){const dx=e.clientX-panState.current.lastMX,dy=e.clientY-panState.current.lastMY;panState.current.panX+=dx;panState.current.panY+=dy;panState.current.lastMX=e.clientX;panState.current.lastMY=e.clientY;applyVPTransform();return;}
    const p=gp(e);wp("cursorX",p.x);wp("cursorY",p.y);setCurPos(p);
    // Detect if Move tool is hovering near a guide for cursor change
    if(toolR.current===T.MOVE&&!drag.current){
      const gs=guidesRef.current;let near=null;
      for(const g of gs){
        const dist=g.type==="h"?Math.abs(p.y-g.pos):Math.abs(p.x-g.pos);
        if(dist<=5){near=g.type;break;}
      }
      setNearGuide(near);
    } else if(!drag.current||drag.current.type!=="guide"){setNearGuide(null);}
    // Transform handle cursor feedback
    if(toolR.current===T.TRANSFORM&&!drag.current){
      const tf=eng.current?.s.tfm;
      if(tf){
        const hs=10;
        const hx2=tf.x,hy2=tf.y,hw=tf.w,hh=tf.h;
        const rot3=(tf.rot||0)*Math.PI/180;
        const tcx=hx2+hw/2,tcy=hy2+hh/2;
        const cos3=Math.cos(-rot3),sin3=Math.sin(-rot3);
        const lx=(p.x-tcx)*cos3-(p.y-tcy)*sin3,ly=(p.x-tcx)*sin3+(p.y-tcy)*cos3;
        const localP={x:lx+tcx,y:ly+tcy};
        // Check rotation handle
        const rlx=(p.x-tcx)*cos3-(p.y-tcy)*sin3,rly=(p.x-tcx)*sin3+(p.y-tcy)*cos3;
        if(Math.abs(rlx)<hs&&Math.abs(rly-(-hh/2-22))<hs){setTfmCursor("alias");}
        // Check corner handles (diagonal resize)
        else if((Math.abs(localP.x-hx2)<hs&&Math.abs(localP.y-hy2)<hs)||(Math.abs(localP.x-hx2-hw)<hs&&Math.abs(localP.y-hy2-hh)<hs)){setTfmCursor("nwse-resize");}
        else if((Math.abs(localP.x-hx2-hw)<hs&&Math.abs(localP.y-hy2)<hs)||(Math.abs(localP.x-hx2)<hs&&Math.abs(localP.y-hy2-hh)<hs)){setTfmCursor("nesw-resize");}
        // Check edge handles
        else if(Math.abs(localP.x-hx2-hw/2)<hs&&(Math.abs(localP.y-hy2)<hs||Math.abs(localP.y-hy2-hh)<hs)){setTfmCursor("ns-resize");}
        else if((Math.abs(localP.x-hx2)<hs||Math.abs(localP.x-hx2-hw)<hs)&&Math.abs(localP.y-hy2-hh/2)<hs){setTfmCursor("ew-resize");}
        // Inside = move
        else if(localP.x>=hx2&&localP.x<=hx2+hw&&localP.y>=hy2&&localP.y<=hy2+hh){setTfmCursor("move");}
        else{setTfmCursor(null);}
      } else {setTfmCursor(null);}
    } else if(toolR.current!==T.TRANSFORM){setTfmCursor(null);}
    // Sample pixel color under cursor
    // Sample pixel color under cursor (single-thread only — canvas is transferred in worker mode)
    if(!workerModeRef.current){const c=cvs.current;if(c){try{const px=c.getContext("2d",{willReadFrequently:true}).getImageData(Math.max(0,Math.min(c.width-1,Math.round(p.x))),Math.max(0,Math.min(c.height-1,Math.round(p.y))),1,1).data;setCurRGB({r:px[0],g:px[1],b:px[2]});}catch(err){}}}
    if(ss.current&&sd.current){
      const s2=ss.current;
      let sx=Math.min(s2.x,p.x),sy=Math.min(s2.y,p.y),sw=Math.abs(p.x-s2.x),sh=Math.abs(p.y-s2.y);
      // Snap selection edges to guides
      const snapped=snapRect(sx,sy,sw,sh);
      sd.current={...sd.current,x:snapped.x,y:snapped.y,w:snapped.w,h:snapped.h};
    }
    if(toolR.current===T.LASSO&&sd.current&&sd.current.t==="lasso"){lassoRef.current.push({x:p.x,y:p.y});sd.current={...sd.current,points:[...lassoRef.current]};}
    if(toolR.current===T.POLY_LASSO&&polyRef.current.length>0){sd.current={t:"polyLasso",points:[...polyRef.current,{x:p.x,y:p.y}],x:0,y:0,w:0,h:0};}
    // Pen tool: detect hover over anchor/handle points and segments for cursor hint
    if(toolR.current===T.PEN&&!penDragRef.current){
      const pathList=penPathRef.current;
      const hit=hitTestPoints(pathList,p.x,p.y,8);
      if(hit){
        // Check if hovering first point of active open path (close hint)
        const activePI=activePathIdxRef.current!=null?activePathIdxRef.current:(pathList.length-1);
        const activePath=pathList[activePI];
        if(hit.handle==="anchor"&&hit.pointIdx===0&&hit.pathIdx===activePI&&activePath&&activePath.points.length>=3&&!activePath.closed){
          setPenHover("close");
        } else if(hit.handle==="anchor"&&e.altKey){
          setPenHover("corner"); // Alt+click will convert to corner
        } else {
          setPenHover(hit.handle==="anchor"?"anchor":"handle");
        }
      } else {
        // Check if hovering near a bezier segment (add point hint)
        const segHit=hitTestSegment(pathList,p.x,p.y,8);
        setPenHover(segHit?"segment":null);
      }
    }
    // Pen tool drag (PathList-aware: penDragRef has pathIdx + pointIdx)
    if(toolR.current===T.PEN&&penDragRef.current){
      const pd=penDragRef.current;
      const pi=pd.pathIdx;
      const altIndependent=pd.altKey||e.altKey; // Alt = independent handle (no mirror)
      if(pd.handle==="new"){
        const dx=p.x-pd.startX,dy=p.y-pd.startY;
        setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>({...pt,cp1x:pt.x-dx,cp1y:pt.y-dy,cp2x:pt.x+dx,cp2y:pt.y+dy})));
      } else if(pd.handle==="cp1"){
        if(altIndependent){
          // Independent: only move cp1
          setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>({...pt,cp1x:p.x,cp1y:p.y})));
        } else {
          // Mirror: move cp1 and mirror cp2 around anchor
          setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>{
            const dx=p.x-pt.x,dy=p.y-pt.y;
            return{...pt,cp1x:p.x,cp1y:p.y,cp2x:pt.x-dx,cp2y:pt.y-dy};
          }));
        }
      } else if(pd.handle==="cp2"){
        if(altIndependent){
          setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>({...pt,cp2x:p.x,cp2y:p.y})));
        } else {
          setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>{
            const dx=p.x-pt.x,dy=p.y-pt.y;
            return{...pt,cp2x:p.x,cp2y:p.y,cp1x:pt.x-dx,cp1y:pt.y-dy};
          }));
        }
      } else if(pd.handle==="anchor"){
        const dx=p.x-pd.origX,dy=p.y-pd.origY;
        setPenPath(pl=>updatePoint(pl,pi,pd.pointIdx,pt=>({...pt,x:p.x,y:p.y,
          cp1x:pt.cp1x!=null?pt.cp1x+dx:null,cp1y:pt.cp1y!=null?pt.cp1y+dy:null,
          cp2x:pt.cp2x!=null?pt.cp2x+dx:null,cp2y:pt.cp2y!=null?pt.cp2y+dy:null})));
        pd.origX=p.x;pd.origY=p.y;
      }
    }
    const dr=drag.current;
    if(dr){
      // Track modifier keys live for shift-constrain, etc.
      if(dr.shiftKey!==undefined)dr.shiftKey=e.shiftKey;
      if(dr.type==="move"){
        let dx=p.x-dr.startX,dy=p.y-dr.startY;
        // Shift = constrain to H or V axis (whichever has more movement)
        if(e.shiftKey){
          if(Math.abs(dx)>Math.abs(dy))dy=0;
          else dx=0;
        }
        // Snap move offset to guides
        const snX=snapToGuides(dx,"v"),snY=snapToGuides(dy,"h");
        dr.dx=snX!==dx?snX:dx;dr.dy=snY!==dy?snY:dy;
      }
      else if(dr.type==="crop"){
        let cx2=Math.min(dr.startX,p.x),cy2=Math.min(dr.startY,p.y),cw2=Math.abs(p.x-dr.startX),ch2=Math.abs(p.y-dr.startY);
        const snapped=snapRect(cx2,cy2,cw2,ch2);
        dr.x=snapped.x;dr.y=snapped.y;dr.w=snapped.w;dr.h=snapped.h;
      }
      else if(dr.type==="tfm"){
        // Compute new transform values — same math for both modes
        const dx=p.x-dr.startX,dy=p.y-dr.startY,h2=dr.handle;
        const altFromCenter=e.altKey;
        let newX=dr.origX,newY=dr.origY,newW=dr.origW,newH=dr.origH,newRot=dr.origRot||0;
        if(h2==="rotate"){
          const angle1=Math.atan2(dr.startY-dr.cy,dr.startX-dr.cx);
          const angle2=Math.atan2(p.y-dr.cy,p.x-dr.cx);
          newRot=dr.origRot+(angle2-angle1)*180/Math.PI;
          if(dr.shiftKey)newRot=Math.round(newRot/15)*15;
        }
        else if(h2==="move"){newX=dr.origX+dx;newY=dr.origY+dy;}
        else{
          if(h2==="br"||h2==="r"||h2==="tr"){newW=Math.max(10,dr.origW+dx);}
          if(h2==="bl"||h2==="l"||h2==="tl"){newX=dr.origX+dx;newW=Math.max(10,dr.origW-dx);}
          if(h2==="br"||h2==="b"||h2==="bl"){newH=Math.max(10,dr.origH+dy);}
          if(h2==="tl"||h2==="t"||h2==="tr"){newY=dr.origY+dy;newH=Math.max(10,dr.origH-dy);}
          if(dr.shiftKey&&(h2==="tl"||h2==="tr"||h2==="bl"||h2==="br")){
            const aspect=dr.origW/dr.origH;
            if(Math.abs(dx)>Math.abs(dy)){newH=newW/aspect;}
            else{newW=newH*aspect;}
            if(h2==="tl"||h2==="bl")newX=dr.origX+dr.origW-newW;
            if(h2==="tl"||h2==="tr")newY=dr.origY+dr.origH-newH;
          }
          if(altFromCenter&&h2!=="move"){
            const ocx=dr.origX+dr.origW/2,ocy=dr.origY+dr.origH/2;
            newX=ocx-newW/2;newY=ocy-newH/2;
          }
        }
        // Apply: single-thread mutates tf directly, worker writes to SAB for overlay + stores for onUp
        const tf=eng.current?.s.tfm;
        if(tf){
          tf.x=newX;tf.y=newY;tf.w=newW;tf.h=newH;if(h2==="rotate")tf.rot=newRot;
        }
        if(dr.isWorker){
          // Write to readback SAB so overlay can render the updated transform
          const rv=readbackViewRef.current;
          if(rv){
            const base=RB_TFM_DATA;
            rv.setFloat32(base,newX,true);rv.setFloat32(base+4,newY,true);
            rv.setFloat32(base+8,newW,true);rv.setFloat32(base+12,newH,true);
            if(h2==="rotate")rv.setFloat32(base+16,newRot,true);
          }
          // Store pending values for TFM_UPDATE on mouseUp
          dr._pending={x:newX,y:newY,w:newW,h:newH,rot:h2==="rotate"?newRot:undefined};
        }
      }
      else if(dr.type==="gradient"){dr.endX=p.x;dr.endY=p.y;}
      else if(dr.type==="penShape"){
        dr.endX=p.x;dr.endY=p.y;
        const bx=Math.min(dr.startX,p.x),by=Math.min(dr.startY,p.y);
        const bw=Math.abs(p.x-dr.startX),bh=Math.abs(p.y-dr.startY);
        if(bw>5&&bh>5){
          const newShape=genShape(dr.shape,bx,by,bw,bh);
          const existing=dr.existingPaths||[];
          setPenPath([...existing,newShape]);
        }
      }
      else if(dr.type==="penTfm"){
        const origPaths=dr.origPaths;const b=dr.bounds;const tpi=dr.targetPathIdx;
        const applyToTarget=(fn)=>{
          setPenPath(origPaths.map((pa,i)=>i===tpi?transformPath(pa,fn):pa));
        };
        if(dr.mode==="move"){
          const dx=p.x-dr.startX,dy=p.y-dr.startY;
          applyToTarget((x,y)=>({x:x+dx,y:y+dy}));
        } else if(dr.mode==="scale"){
          const h=dr.handle;
          let sx=1,sy=1;
          if(h==="br"){sx=(p.x-b.x)/b.w;sy=(p.y-b.y)/b.h;}
          else if(h==="bl"){sx=(b.x+b.w-p.x)/b.w;sy=(p.y-b.y)/b.h;}
          else if(h==="tr"){sx=(p.x-b.x)/b.w;sy=(b.y+b.h-p.y)/b.h;}
          else if(h==="tl"){sx=(b.x+b.w-p.x)/b.w;sy=(b.y+b.h-p.y)/b.h;}
          if(Math.abs(sx)<0.01)sx=0.01;if(Math.abs(sy)<0.01)sy=0.01;
          let ax=b.x,ay=b.y;
          if(h==="tl"){ax=b.x+b.w;ay=b.y+b.h;}
          else if(h==="tr"){ax=b.x;ay=b.y+b.h;}
          else if(h==="bl"){ax=b.x+b.w;ay=b.y;}
          else if(h==="br"){ax=b.x;ay=b.y;}
          applyToTarget((x,y)=>({x:ax+(x-ax)*sx,y:ay+(y-ay)*sy}));
        } else if(dr.mode==="rotate"){
          const angle=Math.atan2(p.y-b.cy,p.x-b.cx)-dr.startAngle;
          const cos=Math.cos(angle),sin=Math.sin(angle);
          applyToTarget((x,y)=>{const dx=x-b.cx,dy=y-b.cy;return{x:b.cx+dx*cos-dy*sin,y:b.cy+dx*sin+dy*cos};});
        }
      }
      else if(dr.type==="guide"){
        const gs=[...guidesRef.current];
        const newPos=dr.guideType==="h"?Math.round(p.y):Math.round(p.x);
        gs[dr.idx]={...gs[dr.idx],pos:newPos};
        guidesRef.current=gs;
      }
    }
  },[gp,wp,applyVPTransform]);

  const onUp=useCallback(()=>{
    if(panState.current.isRotating){panState.current.isRotating=false;return;}
    if(panState.current.isPanning){panState.current.isPanning=false;return;}
    wp("cursorDown",0);
    const tl=toolR.current;
    // Pen tool drag end
    if(tl===T.PEN&&penDragRef.current){penDragRef.current=null;}
    if(tl===T.BRUSH||tl===T.ERASER||tl===T.RUBBER_STAMP||tl===T.DODGE||tl===T.BURN||tl===T.DISTORT)pc(C.STROKE_OFF);
    const additive=ss.current?._additive||false;
    const subtract=ss.current?._subtract||false;
    if(sd.current){
      const d=sd.current;
      if(d.t==="lasso"&&lassoRef.current.length>3){pc(C.SEL_LASSO,{points:lassoRef.current,add:additive,sub:subtract});lassoRef.current=[];}
      else if(d.t==="polyLasso"){sd.current=d;/* keep alive — closed on dblclick */}
      else if((d.t==="rect"||d.t==="ellipse")&&ss.current&&d.w>2&&d.h>2){pc(d.t==="rect"?C.SEL_RECT:C.SEL_ELLIPSE,{x:d.x,y:d.y,w:d.w,h:d.h,add:additive,sub:subtract});}
    }
    ss.current=null;if(!sd.current||sd.current.t!=="polyLasso")sd.current=null;
    const dr=drag.current;
    if(dr){
      if(dr.type==="move"&&(Math.abs(dr.dx)>1||Math.abs(dr.dy)>1))pc(C.MOVE,{dx:Math.round(dr.dx),dy:Math.round(dr.dy)});
      else if(dr.type==="crop"&&dr.w>5&&dr.h>5){pc(C.CROP,{x:Math.round(dr.x),y:Math.round(dr.y),w:Math.round(dr.w),h:Math.round(dr.h)});fu();}
      else if(dr.type==="gradient"){
        const dist=Math.sqrt((dr.endX-dr.startX)**2+(dr.endY-dr.startY)**2);
        if(dist>3){
          pc(C.FILL,{x:dr.startX,y:dr.startY,x2:dr.endX,y2:dr.endY,mode:dr.mode,r:dr.r,g:dr.g,b:dr.b,r2:dr.r2,g2:dr.g2,b2:dr.b2,opacity:dr.opacity,stops:dr.stops});fu();
        }
      }
      else if(dr.type==="penShape"){
        const bw=Math.abs(dr.endX-dr.startX),bh=Math.abs(dr.endY-dr.startY);
        if(bw<5||bh<5){
          // Too small, revert to existing paths
          setPenPath(dr.existingPaths||[]);
        }
        // Keep preset active so user can draw more shapes
      }
      else if(dr.type==="penTfm"){/* applied live */}
      else if(dr.type==="tfm"&&dr.isWorker&&dr._pending){
        // Worker mode: push the accumulated transform values to the engine
        pc(C.TFM_UPDATE,dr._pending);
      }
      else if(dr.type==="guide"){
        // If dragged off canvas, remove the guide; otherwise keep at new position
        const gs=[...guidesRef.current];
        const g=gs[dr.idx];
        const dim=g.type==="h"?ch:cw;
        if(g.pos<0||g.pos>dim){gs.splice(dr.idx,1);}
        setGuides(gs);
      }
      drag.current=null;
    }
    fu();
  },[wp,pc,fu]);

  const onLeave=useCallback(()=>{wp("cursorDown",0);wp("cursorX",-99);wp("cursorY",-99);panState.current.isPanning=false;pc(C.STROKE_OFF);},[wp,pc]);

  const onDblClick=useCallback(e=>{
    // R+double-click = reset canvas rotation
    if(rKeyHeldRef.current){panState.current.rotation=0;applyVPTransform();setRotDisplay(0);return;}
    // Pen tool: double-click to close the active path
    if(toolR.current===T.PEN){
      const pathList=penPathRef.current;
      const activePI=activePathIdxRef.current!=null?activePathIdxRef.current:(pathList.length-1);
      const activePath=pathList[activePI];
      if(activePath&&activePath.points.length>=3&&!activePath.closed){
        // Remove the last point (added by first click of double-click), then close
        const trimmedPts=activePath.points.slice(0,-1);
        const closedPath={...activePath,points:[...trimmedPts,mkPt(trimmedPts[0].x,trimmedPts[0].y)],closed:true};
        setPenPathWithUndo(pl=>pl.map((pa,i)=>i===activePI?closedPath:pa));
        penDragRef.current=null;
        return;
      }
    }
    if(toolR.current===T.POLY_LASSO&&polyRef.current.length>2){
      const additive=ss.current?._additive||false;
      const subtract=ss.current?._subtract||false;
      pc(C.SEL_POLY,{points:polyRef.current,add:additive,sub:subtract});
      polyRef.current=[];sd.current=null;ss.current=null;fu();
    }
  },[pc,fu]);

  return { gp, onDown, onMove, onUp, onLeave, onDblClick };
}
